import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Drawer,
  useTheme,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { Header } from "@/components/molecules/Header";
import { Category, Product } from "@/types";
import { useAppSelector } from "@/core/hooks";
import { useCart } from "@/hooks/useCart";
import {
  useRenderPerformance,
  logBundleInfo,
} from "@/components/utils/performance";
import { DishListProps } from "./DishList.types";
import { CategoryNavigation } from "./components/CategoryNavigation/CategoryNavigation";
import { CategorySection } from "./components/CategorySection/CategorySection";
import { FloatingCartButton } from "./components/FloatingCartButton/FloatingCartButton";
import { useCategories } from "./hooks/useCategories";
import { useProducts } from "./hooks/useProducts";
import { PreorderForm } from "@/components/organisms/PreorderForm";
import { useParentCommunication } from "@/hooks/useParentCommunication";
import { MessageType } from "@/types/iframe-messages";

/**
 * Main DishList Component
 */
const DishListComponent: React.FC<DishListProps> = ({
  onProductSelect,
  onCategorySelect,
  showCategoryNavigation = true,
  onRefresh,
  className,
  "data-testid": dataTestId,
  ...props
}) => {
  // Performance monitoring
  useRenderPerformance("DishList");
  const { sendMessage } = useParentCommunication();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Log bundle info in development
  React.useEffect(() => {
    logBundleInfo("DishList");
  }, []);

  // Get persisted values from Redux state
  const { brandId, pointId } = useAppSelector((state) => state.app);

  // State for cart overlay
  const [isCartOpen, setIsCartOpen] = useState(false);

  // State for active category tracking
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  // Flag to prevent auto-scroll when manually navigating categories
  const manualNavigationRef = useRef(false);

  // Refs for category sections to enable scrolling and intersection observation
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Debounce timer refs - moved to component level
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cart state and operations
  const { totalSum, itemCount, isEmpty, addItem } = useCart();

  // Fetch categories and products using GraphQL hooks
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useCategories({ brandId });

  const {
    products,
    loading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useProducts({ brandId, pointId });

  // Combined loading and error states
  const loading = categoriesLoading || productsLoading;
  const error = categoriesError || productsError;

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped = new Map<
      string,
      { category: Category; products: Product[] }
    >();

    // Initialize with categories
    categories.forEach((category) => {
      grouped.set(category.id, { category, products: [] });
    });

    // Group products by their category bindings
    products.forEach((product) => {
      product.categoryBinds?.forEach((bind) => {
        const categoryData = grouped.get(bind.categoryId);
        if (categoryData) {
          categoryData.products.push(product);
        }
      });
    });

    return Array.from(grouped.values()).filter(
      (item) => item.products.length > 0
    );
  }, [categories, products]);

  // Intersection Observer for tracking visible categories
  React.useEffect(() => {
    if (!scrollContainerRef.current || productsByCategory.length === 0) {
      return;
    }

    // Simple scroll-based approach for better accuracy
    const handleScroll = () => {
      if (manualNavigationRef.current) return;

      // Clear previous debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the scroll handler
      debounceTimerRef.current = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;

        // Special case: if we're at the very top, select first category
        if (scrollTop <= 50) {
          const firstCategoryId = productsByCategory[0].category.id;
          if (activeCategoryId !== firstCategoryId) {
            setActiveCategoryId(firstCategoryId);
          }
          return;
        }

        // Find the category that is most visible in the top portion of the container
        let bestCategory: { id: string; score: number } | null = null;
        const topThreshold = containerRect.top + 100; // Consider top 100px as priority area

        productsByCategory.forEach((section) => {
          const element = categoryRefs.current[section.category.id];
          if (!element) return;

          const rect = element.getBoundingClientRect();

          // Calculate how much of the category is visible
          const visibleTop = Math.max(rect.top, containerRect.top);
          const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          if (visibleHeight > 0) {
            // Score based on visibility and position
            let score = visibleHeight;

            // Bonus for being in the top area
            if (rect.top <= topThreshold) {
              score += 200;
            }

            // Penalty for being far from the top
            const distanceFromTop = Math.max(0, rect.top - containerRect.top);
            score -= distanceFromTop * 0.5;

            if (!bestCategory || score > bestCategory.score) {
              bestCategory = { id: section.category.id, score };
            }
          }
        });

        if (bestCategory && bestCategory.id !== activeCategoryId) {
          setActiveCategoryId(bestCategory.id);
        }
      }, 150); // 150ms debounce
    };

    const container = scrollContainerRef.current;
    container.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);

      // Clear timers on cleanup
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (scrollDebounceTimerRef.current) {
        clearTimeout(scrollDebounceTimerRef.current);
      }
    };
  }, [productsByCategory, activeCategoryId]);

  // Set initial active category
  React.useEffect(() => {
    if (productsByCategory.length > 0 && !activeCategoryId) {
      setActiveCategoryId(productsByCategory[0].category.id);
    }
  }, [productsByCategory, activeCategoryId]);

  // Handle category selection - now scrolls and manages manual navigation
  const handleCategorySelect = useCallback(
    (category: Category) => {
      // Set manual navigation flag to prevent intersection observer updates
      manualNavigationRef.current = true;

      // Update active category immediately
      setActiveCategoryId(category.id);

      const categoryElement = categoryRefs.current[category.id];
      if (categoryElement) {
        categoryElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      // Reset manual navigation flag after scroll completes
      setTimeout(() => {
        manualNavigationRef.current = false;
      }, 1000);

      onCategorySelect?.(category);
    },
    [onCategorySelect]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetchCategories();
    refetchProducts();
    onRefresh?.();
  }, [refetchCategories, refetchProducts, onRefresh]);

  // Handle add to cart
  const handleAddToCart = useCallback(
    (product: Product) => {
      addItem(product, 1);
    },
    [addItem]
  );

  // Handle cart button click
  const handleCartClick = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  // Handle close cart
  const handleCloseCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    sendMessage("REQUEST_CLOSE");
  }, [sendMessage]);

  return (
    <Box
      className={className}
      data-testid={dataTestId}
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "100vh",
        maxWidth: "100%",
        overflow: "hidden",
        flex: 1,
      }}
      {...props}
    >
      {/* Header */}
      <Header
        title="ПРЕДЗАКАЗ"
        transparent
        showBackButton={true}
        onBackClick={handleCloseModal}
      />

      {/* Category Navigation */}
      {showCategoryNavigation && categories.length > 0 && (
        <CategoryNavigation
          categories={categories}
          selectedCategoryId={activeCategoryId || undefined}
          onCategorySelect={handleCategorySelect}
          loading={categoriesLoading}
        />
      )}

      <Divider />

      {/* Main Content */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Box component="main">
          {/* Error State */}
          {error && !loading && (
            <Box
              sx={{
                textAlign: "center",
                py: theme.spacing(4),
              }}
            >
              <Typography variant="h6" color="error" gutterBottom>
                Ошибка загрузки
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: theme.spacing(2) }}
              >
                {error.message || "Произошла ошибка при загрузке данных"}
              </Typography>
              <Button variant="outlined" onClick={handleRefresh}>
                Попробовать снова
              </Button>
            </Box>
          )}

          {/* Products by Category */}
          {!error && (
            <>
              {productsByCategory.length === 0 && !loading && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: theme.spacing(8),
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Блюда не найдены
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    В данный момент нет доступных блюд для отображения
                  </Typography>
                </Box>
              )}

              {productsByCategory.map((section) => (
                <Box
                  key={section.category.id}
                  data-category-id={section.category.id}
                  ref={(el: HTMLDivElement | null) => {
                    categoryRefs.current[section.category.id] = el;
                  }}
                >
                  <CategorySection
                    category={section.category}
                    products={section.products}
                    onProductSelect={onProductSelect}
                    onAddToCart={handleAddToCart}
                    loading={loading}
                  />
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* Floating Cart Button */}
      <FloatingCartButton
        itemCount={itemCount}
        totalSum={totalSum}
        isEmpty={isEmpty}
        onClick={handleCartClick}
      />

      {/* Cart Drawer */}
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={isCartOpen}
        onClose={handleCloseCart}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: theme.spacing(50) },
            maxWidth: "100vw",
            backgroundColor: "transparent",
            backgroundImage: "none",
            boxShadow: "none",
            p: {
              xs: 0,
              sm: 1,
            },
            pt: {
              xs: 2,
            },
            "&:before": {
              content: "''",
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "background.paper",
              opacity: 0.5,
              width: 80,
              height: 5,
              borderRadius: "3px",
              display: {
                xs: "block",
                sm: "none",
              },
            },
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: {
              xs: "30px 30px 0 0",
              sm: "24px",
            },
            display: "flex",
            flex: 1,
            overflow: "hidden",
          }}
        >
          <PreorderForm
            recommendedItems={[]}
            onEditItem={(itemId) => console.log("Edit item:", itemId)}
            onClose={handleCloseCart}
            onAddMore={handleCloseCart}
            onItemSelect={(itemId) => console.log("Select item:", itemId)}
          />
        </Box>
      </Drawer>
    </Box>
  );
};

// Set display name for debugging
DishListComponent.displayName = "DishList";

// Apply memoization with performance monitoring
export const DishList = DishListComponent;
